import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	AddImageBody,
	CreateAdditionalServiceBody,
	CreatePropertyBody,
	GenerateUploadUrlBody,
	GenerateUploadUrlResponse,
	ListPropertiesParams,
	ListPropertiesResponse,
	Property,
	PropertyAdditionalService,
	PropertyImage,
	PropertyUtility,
	ReorderImagesBody,
	UpdateAdditionalServiceBody,
	UpdatePropertyBody,
	UpdatePropertyStatusBody,
	UpsertRentTermsBody,
	UpsertUtilityBody,
	VatDecision,
	ServiceVatRateParams,
	VatRateParams,
} from './types/property'

export const propertiesApi = createApi({
	reducerPath: 'propertiesApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Property', 'PropertyImage', 'PropertyUtility', 'PropertyService', 'VatRate'],
	endpoints: (builder) => ({
		// The rate does not depend on the amount, so it is fetched once per
		// premises kind. Changing the tax regime lives in a different createApi
		// slice (accountApi), so no tag can reach here on its own — the
		// tax-regime dialog dispatches an explicit VatRate invalidation after a
		// successful save; without it the cache serves the old regime's rate.
		getVatRate: builder.query<VatDecision, VatRateParams>({
			query: (params) => ({ url: '/properties/vat-rate', params }),
			providesTags: ['VatRate'],
		}),

		// Same tag as the property rate: both answers move the moment the
		// account's regime does, and the tax-regime dialog invalidates it.
		getServiceVatRate: builder.query<VatDecision, ServiceVatRateParams>({
			query: (params) => ({ url: '/properties/service-vat-rate', params }),
			providesTags: ['VatRate'],
		}),

		listProperties: builder.query<ListPropertiesResponse, ListPropertiesParams | void>({
			query: (params) => ({ url: '/properties', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.properties.map((p) => ({ type: 'Property' as const, id: p.id })),
							{ type: 'Property' as const, id: 'LIST' },
						]
					: [{ type: 'Property' as const, id: 'LIST' }],
		}),

		getProperty: builder.query<Property | null, string>({
			query: (id) => `/properties/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Property', id }],
		}),

		createProperty: builder.mutation<Property | null, CreatePropertyBody>({
			query: (body) => ({ url: '/properties', method: 'POST', body }),
			invalidatesTags: [{ type: 'Property', id: 'LIST' }],
		}),

		updateProperty: builder.mutation<Property | null, { id: string; body: UpdatePropertyBody }>({
			query: ({ id, body }) => ({ url: `/properties/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'Property', id: 'LIST' },
			],
		}),

		updatePropertyStatus: builder.mutation<
			Property | null,
			{ id: string; body: UpdatePropertyStatusBody }
		>({
			query: ({ id, body }) => ({ url: `/properties/${id}/status`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'Property', id: 'LIST' },
			],
		}),

		deleteProperty: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/properties/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Property', id },
				{ type: 'Property', id: 'LIST' },
			],
		}),

		// ── Sub-resources ─────────────────────────────────────

		generateUploadUrl: builder.mutation<GenerateUploadUrlResponse, GenerateUploadUrlBody>({
			query: (body) => ({ url: '/properties/upload-url', method: 'POST', body }),
		}),

		upsertRentTerms: builder.mutation<Property | null, { id: string; body: UpsertRentTermsBody }>({
			query: ({ id, body }) => ({ url: `/properties/${id}/rent-terms`, method: 'PUT', body }),
			invalidatesTags: (_r, _e, { id }) => [{ type: 'Property', id }],
		}),

		addPropertyImage: builder.mutation<PropertyImage, { id: string; body: AddImageBody }>({
			query: ({ id, body }) => ({ url: `/properties/${id}/images`, method: 'POST', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyImage', id },
			],
		}),

		removePropertyImage: builder.mutation<OkResponse, { id: string; imageId: string }>({
			query: ({ id, imageId }) => ({
				url: `/properties/${id}/images/${imageId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyImage', id },
			],
		}),

		reorderPropertyImages: builder.mutation<OkResponse, { id: string; body: ReorderImagesBody }>({
			query: ({ id, body }) => ({
				url: `/properties/${id}/images/reorder`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyImage', id },
			],
		}),

		upsertPropertyUtility: builder.mutation<
			PropertyUtility,
			{ id: string; body: UpsertUtilityBody }
		>({
			query: ({ id, body }) => ({ url: `/properties/${id}/utilities`, method: 'PUT', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyUtility', id },
			],
		}),

		deletePropertyUtility: builder.mutation<OkResponse, { id: string; utilityId: string }>({
			query: ({ id, utilityId }) => ({
				url: `/properties/${id}/utilities/${utilityId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyUtility', id },
			],
		}),

		createAdditionalService: builder.mutation<
			PropertyAdditionalService,
			{ id: string; body: CreateAdditionalServiceBody }
		>({
			query: ({ id, body }) => ({ url: `/properties/${id}/services`, method: 'POST', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyService', id },
			],
		}),

		updateAdditionalService: builder.mutation<
			PropertyAdditionalService,
			{ id: string; serviceId: string; body: UpdateAdditionalServiceBody }
		>({
			query: ({ id, serviceId, body }) => ({
				url: `/properties/${id}/services/${serviceId}`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyService', id },
			],
		}),

		deleteAdditionalService: builder.mutation<OkResponse, { id: string; serviceId: string }>({
			query: ({ id, serviceId }) => ({
				url: `/properties/${id}/services/${serviceId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Property', id },
				{ type: 'PropertyService', id },
			],
		}),
	}),
})

export const {
	useGetServiceVatRateQuery,
	useGetVatRateQuery,
	useListPropertiesQuery,
	useGetPropertyQuery,
	useCreatePropertyMutation,
	useUpdatePropertyMutation,
	useUpdatePropertyStatusMutation,
	useDeletePropertyMutation,
	useGenerateUploadUrlMutation,
	useUpsertRentTermsMutation,
	useAddPropertyImageMutation,
	useRemovePropertyImageMutation,
	useReorderPropertyImagesMutation,
	useUpsertPropertyUtilityMutation,
	useDeletePropertyUtilityMutation,
	useCreateAdditionalServiceMutation,
	useUpdateAdditionalServiceMutation,
	useDeleteAdditionalServiceMutation,
} = propertiesApi
